import React, { useState, useEffect, useCallback } from "react";
import API from "../services/api";
import { Plus, Trash2, Mail, Users, Truck, AlertTriangle, ShieldCheck, KeyRound, X } from "lucide-react";
import { errMsg } from "./runsheets/util";
import { EXPIRY_FIELDS, ExpiryChip } from "./trucks/util";
import { fmtSyncStamp, SYNC_TONE } from "./timesheets/LogNinjaSync";

const BLANK_DRIVER = { name: "", email: "", phone: "", role: "driver", whatsapp_number: "", username: "", password: "" };

// The vehicle header of NZTA's daily checklist, as it is printed on the form.
// These live on the truck record so every day's check prints under them.
const TRUCK_HEADER = ["make", "fleet_no", "tsl", "cof_expiry", "licence_expiry", "ruc_expiry_km", "service_due",
                      "next_service_date", "next_service_km", "notes"];
const BLANK_TRUCK = { license_plate: "", fleet_no: "", make: "", capacity_cbm: 50 };

// A first name is what the crew will actually type at 5am.
const suggestUsername = (name) =>
  String(name || "").trim().toLowerCase().split(/\s+/)[0].replace(/[^a-z0-9]/g, "");

export default function Settings() {
  const [accounts, setAccounts] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [newAccount, setNewAccount] = useState({ email: "", host: "imap.gmail.com", port: 993, username: "", password: "", use_ssl: true });
  const [newDriver, setNewDriver] = useState(BLANK_DRIVER);
  const [newTruck, setNewTruck] = useState(BLANK_TRUCK);
  // Truck header editor: which truck row is open, and the draft as typed.
  const [truckFor, setTruckFor] = useState(null);
  const [truckDraft, setTruckDraft] = useState({});
  const [error, setError] = useState(null);
  const [loginFor, setLoginFor] = useState(null);            // crew id whose login form is open
  const [loginDraft, setLoginDraft] = useState({ username: "", password: "" });
  const [busy, setBusy] = useState(false);
  // A password just issued by a reset, held only until it is read out and
  // dismissed. Never written to storage - it exists to be spoken aloud once.
  const [issued, setIssued] = useState(null);
  // Hour caps editor: which crew row is open, and the draft values as typed.
  const [capFor, setCapFor] = useState(null);
  const [capDraft, setCapDraft] = useState({ day: "", fortnight: "" });
  // The DeliverEzy portal account the run extraction signs in with. The
  // password is typed here and sent to our own backend; it is never read
  // back - the server only says whether one is on file.
  const [portal, setPortal] = useState(null);
  const [portalDraft, setPortalDraft] = useState({ google_email: "", google_password: "" });
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalNotice, setPortalNotice] = useState(null);
  const [portalError, setPortalError] = useState(null);   // shown beside the form, not only in the page banner
  // The Log Ninja operator account the timesheet sync signs in with to read
  // the drivers' logbook days. Same rule as the portal login: the password
  // goes to our own backend once and is never read back.
  const [ninja, setNinja] = useState(null);
  const [ninjaDraft, setNinjaDraft] = useState({ email: "", password: "" });
  const [ninjaBusy, setNinjaBusy] = useState(false);
  const [ninjaNotice, setNinjaNotice] = useState(null);
  const [ninjaError, setNinjaError] = useState(null);

  const loadNinja = useCallback(async () => {
    try {
      const res = await API.get("/time/logninja/login");
      setNinja(res.data || null);
      setNinjaDraft((d) => ({ ...d, email: res.data?.email || "" }));
    } catch (err) {
      // 403 = not this role's card; 404 = the server has no Log Ninja module
      // yet, so the card simply stays hidden
      if (![403, 404].includes(err?.response?.status)) setError(`/time/logninja/login: ${errMsg(err)}`);
    }
  }, []);

  const saveNinja = async (e) => {
    if (e) e.preventDefault();
    setNinjaBusy(true);
    setError(null);
    setNinjaNotice(null);
    setNinjaError(null);
    try {
      const res = await API.put("/time/logninja/login", {
        email: ninjaDraft.email.trim(),
        password: ninjaDraft.password,
      });
      setNinja(res.data || null);
      setNinjaDraft((d) => ({ ...d, password: "" }));
      setNinjaNotice(ninjaDraft.password
        ? "Log Ninja login saved. The next sync will use it."
        : "Log Ninja email saved; the password on file was kept.");
    } catch (err) {
      setNinjaError(`Not saved: ${errMsg(err)}`);
    } finally {
      setNinjaBusy(false);
    }
  };

  const loadPortal = useCallback(async () => {
    try {
      const res = await API.get("/scraper/login");
      setPortal(res.data || null);
      setPortalDraft((d) => ({ ...d, google_email: res.data?.google_email || "" }));
    } catch (err) {
      if (err?.response?.status !== 403) setError(`/scraper/login: ${errMsg(err)}`);
    }
  }, []);

  const savePortal = async (e) => {
    if (e) e.preventDefault();
    setPortalBusy(true);
    setError(null);
    setPortalNotice(null);
    setPortalError(null);
    try {
      const res = await API.put("/scraper/login", {
        google_email: portalDraft.google_email.trim(),
        google_password: portalDraft.google_password,
      });
      setPortal(res.data || null);
      setPortalDraft((d) => ({ ...d, google_password: "" }));
      setPortalNotice(portalDraft.google_password
        ? "Portal login saved. The next run extraction will use it."
        : "Portal email saved; the password on file was kept.");
    } catch (err) {
      setPortalError(`Not saved: ${errMsg(err)}`);
    } finally {
      setPortalBusy(false);
    }
  };

  // Each list is loaded and reported on separately: three un-awaited promises
  // with no catch meant one failing request threw an unhandled rejection and
  // left a panel silently empty, with nothing on screen to say why.
  const loadAll = useCallback(async () => {
    const load = async (path, setter) => {
      try {
        const res = await API.get(path);
        setter(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        setError(`${path}: ${errMsg(err)}`);
      }
    };
    await Promise.all([
      load("/email-accounts", setAccounts),
      load("/drivers", setDrivers),
      load("/trucks", setTrucks),
    ]);
  }, []);

  useEffect(() => {
    loadPortal();
  }, [loadPortal]);

  useEffect(() => {
    loadNinja();
  }, [loadNinja]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // --- Crew logins -------------------------------------------------------
  // The password is held only until the request goes out: it is never logged,
  // never echoed back by the API, and never written to localStorage.
  const openLogin = (d) => {
    setError(null);
    setLoginFor(d.id);
    setLoginDraft({ username: d.username || suggestUsername(d.name), password: "" });
  };

  const closeLogin = () => {
    setLoginFor(null);
    setLoginDraft({ username: "", password: "" });
  };

  const saveLogin = async (d, e) => {
    if (e) e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await API.post(`/drivers/${d.id}/login`, {
        username: loginDraft.username.trim().toLowerCase(),
        password: loginDraft.password,
      });
      closeLogin();
      await loadAll();
    } catch (err) {
      setError(errMsg(err));
      setLoginDraft({ ...loginDraft, password: "" });
    } finally {
      setBusy(false);
    }
  };

  // The answer to "a driver rang, they cannot get in". Passwords are stored
  // one-way, so nobody can look up what someone's password IS - what an admin
  // can do is set a new one and read it back to them. It is shown once, here,
  // and the account must replace it at next sign-in.
  const resetPassword = async (d) => {
    if (!window.confirm(`Reset ${d.name}'s password back to their first name?\n\nYou will be shown the new password to read out. They must choose their own the next time they sign in.`)) return;
    setBusy(true);
    setError(null);
    try {
      const res = await API.post(`/drivers/${d.id}/login/reset`, {});
      setIssued({ id: d.id, name: d.name, username: res.data.username, password: res.data.password });
      await loadAll();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const openCaps = (d) => {
    setCapFor(d.id);
    setCapDraft({
      day: d.cap_hours_day != null ? String(d.cap_hours_day) : "",
      fortnight: d.cap_hours_fortnight != null ? String(d.cap_hours_fortnight) : "",
    });
  };

  // A cap flags shifts that go over it everywhere hours are shown; it does
  // not stop a driver clocking in - the hours were worked either way.
  const saveCaps = async (d, e) => {
    if (e) e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const num = (v) => (String(v).trim() === "" ? null : Number(v));
      await API.patch(`/drivers/${d.id}/caps`, {
        cap_hours_day: num(capDraft.day),
        cap_hours_fortnight: num(capDraft.fortnight),
      });
      setCapFor(null);
      await loadAll();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const disableLogin = async (d) => {
    if (!window.confirm(`Disable ${d.name}'s login (${d.username})? They will not be able to sign in. The account is kept, because the timesheet audit trail refers to it.`)) return;
    setBusy(true);
    setError(null);
    try {
      await API.delete(`/drivers/${d.id}/login`);
      await loadAll();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const addAccount = async () => {
    if (!newAccount.email || !newAccount.username || !newAccount.password) {
      return alert("Please fill in email, username, and app password.");
    }
    try { 
      await API.post("/email-accounts", newAccount); 
      setNewAccount({ email: "", host: "imap.gmail.com", port: 993, username: "", password: "", use_ssl: true }); 
      loadAll(); 
    } catch (e) { 
      alert("Failed: " + (e.response?.data?.detail || e.message)); 
    }
  };

  const deleteAccount = async (id) => {
    if (!window.confirm("Delete this email account?")) return;
    await API.delete(`/email-accounts/${id}`); 
    loadAll();
  };

  const addDriver = async () => {
    if (!newDriver.name || !newDriver.phone) {
      return alert("Please fill in name and phone.");
    }
    setError(null);
    try {
      await API.post("/drivers", newDriver);
      setNewDriver(BLANK_DRIVER);
      loadAll();
    } catch (e) {
      setError(errMsg(e));
      // Whatever else survives a failed save, the password does not.
      setNewDriver({ ...newDriver, password: "" });
    }
  };

  const deleteDriver = async (id) => {
    if (!window.confirm("Delete this roster record?")) return;
    await API.delete(`/drivers/${id}`); 
    loadAll();
  };

  // Empty text becomes null so the server clears the field instead of
  // storing "" - the printed form shows a blank either way, but the expiry
  // warnings only stay quiet for a date that is really absent.
  const headerBody = (draft) => {
    const body = {};
    TRUCK_HEADER.forEach((k) => {
      const v = String(draft[k] ?? "").trim();
      body[k] = v === "" ? null : v;
    });
    if ("is_active" in draft) body.is_active = !!draft.is_active;
    return body;
  };

  const addTruck = async () => {
    const plate = newTruck.license_plate.trim().toUpperCase();
    if (!plate) {
      setError("Please enter the truck's licence plate.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await API.post("/trucks", { ...newTruck, license_plate: plate });
      // The header fields go on with a second call if the create did not
      // take them, so a fleet number typed here is never silently dropped.
      const created = res.data || {};
      const header = { fleet_no: newTruck.fleet_no, make: newTruck.make };
      const missed = Object.keys(header).filter((k) => String(header[k] || "").trim() && !created[k]);
      if (created.id && missed.length) {
        const patch = {};
        missed.forEach((k) => { patch[k] = header[k].trim(); });
        await API.patch(`/trucks/${created.id}`, patch);
      }
      setNewTruck(BLANK_TRUCK);
      await loadAll();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const openTruck = (t) => {
    setError(null);
    setTruckFor(t.id);
    const draft = { is_active: t.is_active !== false && t.is_active !== 0 };
    TRUCK_HEADER.forEach((k) => { draft[k] = t[k] ? String(t[k]).slice(0, k.endsWith("_expiry") ? 10 : undefined) : ""; });
    setTruckDraft(draft);
  };

  // Retiring a truck (active off) hides it from the driver's truck picker and
  // the fleet list; its checks are kept, which is why there is no delete.
  const saveTruck = async (t, e) => {
    if (e) e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await API.patch(`/trucks/${t.id}`, headerBody(truckDraft));
      setTruckFor(null);
      await loadAll();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="border-b border-slate-800 pb-4">
        <h2 className="text-2xl font-black text-white tracking-tight">System Settings</h2>
        <p className="text-slate-400 text-xs mt-1">Configure email parsing sources, drivers/offsiders database, crew logins, and truck assets.</p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-sm text-red-300 flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span className="flex-1 break-words">{error}</span>
          <button onClick={() => setError(null)} className="text-red-300/70 hover:text-white"><X size={14} /></button>
        </div>
      )}

      {/* Gmail Accounts */}
      <div className="bg-slate-950/40 rounded-2xl shadow border border-slate-800/80 p-5 space-y-4">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm border-b border-slate-800 pb-2">
          <Mail size={17} className="text-blue-400" /> Email Sync Accounts (Dual Gmail Support)
        </h3>
        
        <div className="space-y-2">
          {accounts.length === 0 ? (
            <p className="text-xs text-slate-500 py-2 text-center bg-slate-900/10 rounded-lg border border-dashed border-slate-800">No Gmail accounts synced yet. Sync up to 2 Gmail accounts below.</p>
          ) : (
            accounts.map(a => (
              <div key={a.id} className="flex justify-between items-center p-3 bg-slate-900/40 border border-slate-800/80 rounded-xl text-xs">
                <div className="flex items-center gap-3">
                  <span className="font-bold text-slate-100">{a.email}</span>
                  <span className="text-slate-600">|</span>
                  <span className="text-slate-400 font-mono">{a.host}:{a.port}</span>
                  <span className="text-slate-600">|</span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${a.is_active ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                    {a.is_active ? "Active Scanner" : "Inactive"}
                  </span>
                </div>
                <button onClick={() => deleteAccount(a.id)} className="text-red-400 hover:text-red-300 p-1 bg-red-500/10 hover:bg-red-500/20 border border-red-500/25 rounded-lg transition-all">
                  <Trash2 size={13} />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Add Account Form */}
        <div className="bg-slate-900/30 p-4 rounded-xl border border-slate-800/60 space-y-3">
          <p className="text-xs font-semibold text-slate-300">Add New Gmail Account</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <input 
              placeholder="Gmail Address" 
              className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-blue-500" 
              value={newAccount.email} 
              onChange={e => setNewAccount({...newAccount, email: e.target.value})} 
            />
            <input 
              placeholder="IMAP Host (imap.gmail.com)" 
              className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-blue-500" 
              value={newAccount.host} 
              onChange={e => setNewAccount({...newAccount, host: e.target.value})} 
            />
            <input 
              placeholder="Port (993)" 
              type="number" 
              className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-blue-500" 
              value={newAccount.port} 
              onChange={e => setNewAccount({...newAccount, port: parseInt(e.target.value) || 993})} 
            />
            <input 
              placeholder="Username / Email" 
              className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-blue-500" 
              value={newAccount.username} 
              onChange={e => setNewAccount({...newAccount, username: e.target.value})} 
            />
            <input 
              placeholder="Gmail App Password" 
              type="password" 
              className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-blue-500" 
              value={newAccount.password} 
              onChange={e => setNewAccount({...newAccount, password: e.target.value})} 
            />
            <button 
              onClick={addAccount} 
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 font-bold transition-all flex items-center justify-center gap-1.5 shadow"
            >
              <Plus size={14} /> Add Sync Source
            </button>
          </div>
          <div className="flex items-start gap-2 mt-2 text-[10px] text-slate-400 bg-slate-950/30 p-2.5 rounded-lg border border-slate-800">
            <AlertTriangle size={14} className="text-amber-500 shrink-0 mt-0.5" />
            <p>Gmail accounts require enabling IMAP in Mail Settings and generating an **App Password** via Google Account Security (2FA required). Regular password login will be rejected by Google.</p>
          </div>
        </div>
      </div>

      {/* Roster crew (Drivers & Offsiders) */}
      <div className="bg-slate-950/40 rounded-2xl shadow border border-slate-800/80 p-5 space-y-4">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm border-b border-slate-800 pb-2">
          <Users size={17} className="text-indigo-400" /> Drivers & Offsiders (Roster Management)
        </h3>

        {/* A just-issued password, shown once so it can be read out. It is not
            stored anywhere and cannot be recovered after this panel is closed -
            the server keeps only a one-way hash. Resetting again is the way
            back, not looking it up. */}
        {issued && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 space-y-2">
            <p className="text-xs font-bold text-blue-200">
              New password for {issued.name}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <span className="block text-[10px] uppercase tracking-wide text-slate-400">Username</span>
                <span className="font-mono text-sm text-slate-100">{issued.username}</span>
              </div>
              <div>
                <span className="block text-[10px] uppercase tracking-wide text-slate-400">Password</span>
                <span className="font-mono text-sm text-white bg-slate-950 border border-slate-700 rounded px-2 py-0.5 select-all">
                  {issued.password}
                </span>
              </div>
              <button
                onClick={() => setIssued(null)}
                className="ml-auto text-[11px] font-semibold text-blue-200 hover:text-white px-2.5 py-1 bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/30 rounded-lg transition-all"
              >
                Done — hide it
              </button>
            </div>
            <p className="text-[10px] text-slate-400">
              Read this to them now. It is shown once and cannot be looked up again.
              They will be asked to choose their own password when they next sign in.
            </p>
          </div>
        )}

        <div className="space-y-2">
          {drivers.length === 0 ? (
            <p className="text-xs text-slate-500 py-2 text-center bg-slate-900/10 rounded-lg border border-dashed border-slate-800">No crew rostered yet.</p>
          ) : (
            drivers.map(d => (
              <div key={d.id} className="p-3 bg-slate-900/40 border border-slate-800/80 rounded-xl text-xs border-l-4 border-blue-500 space-y-2">
                <div className="flex justify-between items-center gap-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-bold text-slate-100">{d.name}</span>
                    <span className="text-slate-600">|</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${d.role === "driver" ? "bg-blue-950/50 text-blue-400 border border-blue-500/20" : "bg-teal-950/50 text-teal-400 border border-teal-500/20"}`}>{d.role}</span>
                    <span className="text-slate-600">|</span>
                    <span className="text-slate-400 font-medium">Ph: {d.phone}</span>
                    <span className="text-slate-600">|</span>
                    <span className="text-slate-400 font-medium">WhatsApp: {d.whatsapp_number || "—"}</span>
                    <span className="text-slate-600">|</span>
                    {/* A login is what lets this person clock themselves in.
                        Crew without one still get paid - the office keys their
                        hours into Timesheets by hand. */}
                    {d.has_login ? (
                      <>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${d.login_active ? "bg-green-500/10 text-green-400 border border-green-500/20" : "bg-red-500/10 text-red-400 border border-red-500/20"}`}>
                          {d.login_active ? `login: ${d.username}` : `login disabled: ${d.username}`}
                        </span>
                        {d.must_change_password && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20"
                            title="Still using the password they were handed. They will be asked to choose their own at next sign-in.">
                            handed-out password
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-800 text-slate-400 border border-slate-700">no login</span>
                    )}
                    <span className="text-slate-600">|</span>
                    <span className={`font-semibold ${d.is_online ? "text-green-400" : "text-slate-500"}`}>
                      {d.is_online ? "🟢 Active/Online" : "🔴 Offline"}
                    </span>
                    {d.current_lat && (
                      <>
                        <span className="text-slate-600">|</span>
                        <span className="text-xs font-mono text-slate-400">📍 {d.current_lat.toFixed(4)}, {d.current_lon.toFixed(4)}</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => (loginFor === d.id ? closeLogin() : openLogin(d))}
                      disabled={busy}
                      className="flex items-center gap-1 text-indigo-300 hover:text-indigo-200 px-2 py-1 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/25 rounded-lg transition-all font-semibold disabled:opacity-50"
                      title={d.has_login ? "Reset this login" : "Give this crew member a login"}
                    >
                      <KeyRound size={13} /> {d.has_login ? (d.login_active ? "Reset password" : "Re-enable") : "Add login"}
                    </button>
                    {d.has_login && (
                      <button onClick={() => resetPassword(d)} disabled={busy}
                        className="text-blue-300 hover:text-blue-200 px-2 py-1 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/25 rounded-lg transition-all font-semibold disabled:opacity-50"
                        title="Reset to their first name and show it to me">
                        Reset to first name
                      </button>
                    )}
                    <button onClick={() => (capFor === d.id ? setCapFor(null) : openCaps(d))} disabled={busy}
                      className={`px-2 py-1 rounded-lg transition-all font-semibold disabled:opacity-50 border ${
                        d.cap_hours_day != null || d.cap_hours_fortnight != null
                          ? "text-teal-300 hover:text-teal-200 bg-teal-500/10 hover:bg-teal-500/20 border-teal-500/25"
                          : "text-slate-400 hover:text-slate-200 bg-slate-800/60 hover:bg-slate-800 border-slate-700"}`}
                      title="Maximum hours for this person - flags shifts that go over">
                      {d.cap_hours_day != null || d.cap_hours_fortnight != null
                        ? `Cap ${d.cap_hours_day != null ? `${d.cap_hours_day} h/day` : ""}${d.cap_hours_day != null && d.cap_hours_fortnight != null ? " · " : ""}${d.cap_hours_fortnight != null ? `${d.cap_hours_fortnight} h/fortnight` : ""}`
                        : "Hours cap"}
                    </button>
                    {d.has_login && d.login_active && (
                      <button onClick={() => disableLogin(d)} disabled={busy}
                        className="text-amber-300 hover:text-amber-200 px-2 py-1 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/25 rounded-lg transition-all font-semibold disabled:opacity-50">
                        Disable
                      </button>
                    )}
                    <button onClick={() => deleteDriver(d.id)} className="text-red-400 hover:text-red-300 p-1 bg-red-500/10 hover:bg-red-500/20 border border-red-500/25 rounded-lg transition-all">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>

                {capFor === d.id && (
                  <form onSubmit={(e) => saveCaps(d, e)} className="bg-slate-950/60 border border-slate-800 rounded-xl p-3 space-y-2">
                    <p className="text-[10px] text-slate-400">
                      Maximum hours for {d.name}. Going over a cap flags those shifts on Timesheets, Daily Hours,
                      the payroll workbook and in their own app — it does not stop them clocking in, because the
                      hours worked still have to be recorded. Leave a box empty for no cap.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <label className="text-[10px] text-slate-400">
                        Per day (hours)
                        <input type="number" min="0.5" max="24" step="0.5" value={capDraft.day}
                          onChange={(e) => setCapDraft({ ...capDraft, day: e.target.value })}
                          placeholder="e.g. 10"
                          className="mt-1 w-full bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-teal-500" />
                      </label>
                      <label className="text-[10px] text-slate-400">
                        Per fortnight (hours)
                        <input type="number" min="1" max="336" step="0.5" value={capDraft.fortnight}
                          onChange={(e) => setCapDraft({ ...capDraft, fortnight: e.target.value })}
                          placeholder="e.g. 80"
                          className="mt-1 w-full bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-teal-500" />
                      </label>
                      <div className="flex items-end gap-2">
                        <button type="submit" disabled={busy}
                          className="flex-1 bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white font-semibold rounded-lg px-3 py-2">
                          Save cap
                        </button>
                        <button type="button" onClick={() => setCapFor(null)}
                          className="px-3 py-2 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700">
                          Cancel
                        </button>
                      </div>
                    </div>
                  </form>
                )}
                {loginFor === d.id && (
                  <form onSubmit={(e) => saveLogin(d, e)} className="bg-slate-950/60 border border-slate-800 rounded-xl p-3 space-y-2">
                    <p className="text-[10px] text-slate-400">
                      {d.has_login && !d.login_active
                        ? "Setting a password here switches this login back on."
                        : "The crew member signs in with this username and password on the driver app."}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <input
                        placeholder="Username"
                        autoCapitalize="none"
                        autoCorrect="off"
                        className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-indigo-500"
                        value={loginDraft.username}
                        onChange={e => setLoginDraft({ ...loginDraft, username: e.target.value })}
                      />
                      <input
                        placeholder="Password (8+ characters)"
                        type="password"
                        autoComplete="new-password"
                        className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-indigo-500"
                        value={loginDraft.password}
                        onChange={e => setLoginDraft({ ...loginDraft, password: e.target.value })}
                      />
                      <button type="submit" disabled={busy || !loginDraft.username.trim() || loginDraft.password.length < 8}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-4 py-2 font-bold transition-all flex items-center justify-center gap-1.5 shadow disabled:opacity-50">
                        <KeyRound size={14} /> {busy ? "Saving…" : "Save login"}
                      </button>
                      <button type="button" onClick={closeLogin}
                        className="text-slate-400 hover:text-white rounded-lg px-4 py-2 font-semibold border border-slate-700">
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            ))
          )}
        </div>

        {/* Add Driver Form */}
        <div className="bg-slate-900/30 p-4 rounded-xl border border-slate-800/60 space-y-3">
          <p className="text-xs font-semibold text-slate-300">Roster New Member</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
            <input 
              placeholder="Name" 
              className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-indigo-500" 
              value={newDriver.name} 
              onChange={e => setNewDriver({...newDriver, name: e.target.value})} 
            />
            <input 
              placeholder="Phone" 
              className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-indigo-500" 
              value={newDriver.phone} 
              onChange={e => setNewDriver({...newDriver, phone: e.target.value})} 
            />
            <select 
              className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none cursor-pointer focus:border-indigo-500" 
              value={newDriver.role} 
              onChange={e => setNewDriver({...newDriver, role: e.target.value})}
            >
              <option value="driver">Driver</option>
              <option value="offsider">Offsider</option>
            </select>
            <input 
              placeholder="WhatsApp (+64...)" 
              className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-indigo-500" 
              value={newDriver.whatsapp_number} 
              onChange={e => setNewDriver({...newDriver, whatsapp_number: e.target.value})} 
            />
            <input
              placeholder="Email Address"
              className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-indigo-500 md:col-span-2"
              value={newDriver.email}
              onChange={e => setNewDriver({...newDriver, email: e.target.value})}
            />
            {/* Optional, and deliberately so: an offsider who clocks himself
                in needs an account, one whose hours the office keys in does
                not. A login can always be added later from the list above. */}
            <input
              placeholder="Username (optional)"
              autoCapitalize="none"
              autoCorrect="off"
              className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-indigo-500"
              value={newDriver.username}
              onChange={e => setNewDriver({...newDriver, username: e.target.value})}
            />
            <input
              placeholder="Password (8+ characters)"
              type="password"
              autoComplete="new-password"
              className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-indigo-500"
              value={newDriver.password}
              onChange={e => setNewDriver({...newDriver, password: e.target.value})}
            />
            <button
              onClick={addDriver}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-4 py-2 font-bold transition-all flex items-center justify-center gap-1.5 shadow md:col-span-4"
            >
              <Plus size={14} /> Add Crew Roster
            </button>
          </div>
          <div className="flex items-start gap-2 mt-2 text-[10px] text-slate-400 bg-slate-950/30 p-2.5 rounded-lg border border-slate-800">
            <ShieldCheck size={14} className="text-indigo-400 shrink-0 mt-0.5" />
            <p>A login lets this person sign in on the driver app and clock themselves in. Passwords are stored hashed and are never shown again — to change one, use Reset password above.</p>
          </div>
        </div>
      </div>

      {/* DeliverEzy portal login (the Harvey Norman Commercial run extraction) */}
      {portal && (
        <div className="bg-slate-950/40 rounded-2xl shadow border border-slate-800/80 p-5 space-y-4">
          <h3 className="font-bold text-white flex items-center gap-2 text-sm border-b border-slate-800 pb-2">
            <KeyRound size={17} className="text-emerald-400" /> DeliverEzy Portal Login (run extraction)
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <div className="bg-slate-900/40 border border-slate-800/80 rounded-xl px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Account on file</div>
              <div className="font-semibold text-slate-100 truncate">{portal.google_email || "none"}</div>
              <div className="text-[11px] text-slate-500">password {portal.password_set ? "set" : "not set"}</div>
            </div>
            <div className="bg-slate-900/40 border border-slate-800/80 rounded-xl px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Browser session seeded</div>
              <div className="font-semibold text-slate-100">
                {portal.session_seeded_at ? new Date(`${portal.session_seeded_at}Z`).toLocaleString("en-NZ", { timeZone: "Pacific/Auckland" }) : "never"}
              </div>
              <div className="text-[11px] text-slate-500">from a real sign-in on a PC</div>
            </div>
            <div className="bg-slate-900/40 border border-slate-800/80 rounded-xl px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Last extraction</div>
              <div className={`font-semibold ${portal.extraction?.status === "error" ? "text-red-300" : "text-slate-100"}`}>
                {portal.extraction?.status || "idle"}{portal.extraction?.fetched_at ? ` · data from ${portal.extraction.fetched_at}` : ""}
              </div>
              {portal.extraction?.message && (
                <div className="text-[11px] text-slate-500 line-clamp-2" title={portal.extraction.message}>{portal.extraction.message}</div>
              )}
            </div>
          </div>

          <form onSubmit={savePortal} className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <label className="space-y-1">
              <span className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold">Google email</span>
              <input type="email" required autoComplete="off"
                className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-emerald-500 w-full"
                value={portalDraft.google_email}
                onChange={(e) => setPortalDraft({ ...portalDraft, google_email: e.target.value })} />
            </label>
            <label className="space-y-1">
              <span className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold">Password</span>
              <input type="password" autoComplete="new-password"
                placeholder={portal.password_set ? "leave blank to keep the current one" : "required"}
                className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-emerald-500 w-full"
                value={portalDraft.google_password}
                onChange={(e) => setPortalDraft({ ...portalDraft, google_password: e.target.value })} />
            </label>
            <div className="flex items-end">
              <button type="submit" disabled={portalBusy || !portalDraft.google_email}
                className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg px-4 py-2 font-bold transition-all disabled:opacity-50 w-full">
                {portalBusy ? "Saving…" : "Save portal login"}
              </button>
            </div>
          </form>
          {portalNotice && <p className="text-[11px] text-emerald-300">{portalNotice}</p>}
          {portalError && <p className="text-[11px] text-red-300">{portalError}</p>}

          <div className="flex items-start gap-2 text-[10px] text-slate-400 bg-slate-950/30 p-2.5 rounded-lg border border-slate-800">
            <ShieldCheck size={14} className="text-emerald-400 shrink-0 mt-0.5" />
            <p>
              The password is stored on the server for the extraction only and is never shown again. Google usually refuses an
              automated password sign-in from the server, so when the extraction reports "Google rejected the automated sign-in",
              a fresh session must be seeded from a real browser on a PC (backend/app/scripts/seed_portal_session.py) — the
              "Browser session seeded" date above shows when that was last done.
            </p>
          </div>
        </div>
      )}

      {/* Log Ninja operator login (the drivers' logbook days, read into Timesheets) */}
      {ninja && (
        <div className="bg-slate-950/40 rounded-2xl shadow border border-slate-800/80 p-5 space-y-4">
          <h3 className="font-bold text-white flex items-center gap-2 text-sm border-b border-slate-800 pb-2">
            <KeyRound size={17} className="text-sky-400" /> Log Ninja operator login
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <div className="bg-slate-900/40 border border-slate-800/80 rounded-xl px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Account on file</div>
              <div className="font-semibold text-slate-100 truncate">{ninja.email || "none"}</div>
              <div className="text-[11px] text-slate-500">password {ninja.password_set ? "set" : "not set"}</div>
            </div>
            <div className="bg-slate-900/40 border border-slate-800/80 rounded-xl px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Last sync</div>
              <div className={`font-semibold ${SYNC_TONE[ninja.last_sync?.status] || "text-slate-100"}`}>
                {ninja.last_sync
                  ? `${ninja.last_sync.status}${ninja.last_sync.dry_run ? " (preview)" : ""}`
                  : "never"}
                {ninja.last_sync?.finished_at || ninja.last_sync?.started_at
                  ? ` · ${fmtSyncStamp(ninja.last_sync.finished_at || ninja.last_sync.started_at)}`
                  : ""}
              </div>
              {ninja.last_sync?.message && (
                <div className="text-[11px] text-slate-500 line-clamp-2" title={ninja.last_sync.message}>{ninja.last_sync.message}</div>
              )}
            </div>
            <div className="bg-slate-900/40 border border-slate-800/80 rounded-xl px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Last sync counts</div>
              <div className="font-semibold text-slate-100">
                {ninja.last_sync?.summary
                  ? ["create", "update", "unchanged", "conflicts", "unmatched", "locked", "open", "errors"]
                    .filter((k) => ninja.last_sync.summary[k])
                    .map((k) => `${ninja.last_sync.summary[k]} ${k}`)
                    .join(" · ") || "nothing to do"
                  : "—"}
              </div>
              <div className="text-[11px] text-slate-500">run from Timesheets › Log Ninja sync</div>
            </div>
          </div>

          <form onSubmit={saveNinja} className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <label className="space-y-1">
              <span className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold">Log Ninja email</span>
              <input type="email" required autoComplete="off"
                className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-sky-500 w-full"
                value={ninjaDraft.email}
                onChange={(e) => setNinjaDraft({ ...ninjaDraft, email: e.target.value })} />
            </label>
            <label className="space-y-1">
              <span className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold">Password</span>
              <input type="password" autoComplete="new-password"
                placeholder={ninja.password_set ? "leave blank to keep the current one" : "required"}
                className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-sky-500 w-full"
                value={ninjaDraft.password}
                onChange={(e) => setNinjaDraft({ ...ninjaDraft, password: e.target.value })} />
            </label>
            <div className="flex items-end">
              <button type="submit" disabled={ninjaBusy || !ninjaDraft.email}
                className="bg-sky-600 hover:bg-sky-500 text-white rounded-lg px-4 py-2 font-bold transition-all disabled:opacity-50 w-full">
                {ninjaBusy ? "Saving…" : "Save Log Ninja login"}
              </button>
            </div>
          </form>
          {ninjaNotice && <p className="text-[11px] text-sky-300">{ninjaNotice}</p>}
          {ninjaError && <p className="text-[11px] text-red-300">{ninjaError}</p>}

          <div className="flex items-start gap-2 text-[10px] text-slate-400 bg-slate-950/30 p-2.5 rounded-lg border border-slate-800">
            <ShieldCheck size={14} className="text-sky-400 shrink-0 mt-0.5" />
            <p>
              The dashboard signs in to Log Ninja with this account to read the drivers' logbook days (start, finish and
              rest breaks) into Timesheets. The password is stored only on the server and is never shown again — to
              change it, type a new one here.
            </p>
          </div>
        </div>
      )}

      {/* Trucks Asset List */}
      <div className="bg-slate-950/40 rounded-2xl shadow border border-slate-800/80 p-5 space-y-4">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm border-b border-slate-800 pb-2">
          <Truck size={17} className="text-amber-400" /> Active Truck Assets
        </h3>
        
        <div className="space-y-2">
          {trucks.length === 0 ? (
            <p className="text-xs text-slate-500 py-2 text-center bg-slate-900/10 rounded-lg border border-dashed border-slate-800">No trucks registered.</p>
          ) : (
            trucks.map(t => {
              const retired = t.is_active === false || t.is_active === 0;
              return (
                <div key={t.id} className={`p-3 bg-slate-900/40 border border-slate-800/80 rounded-xl text-xs border-l-4 space-y-2 ${retired ? "border-slate-600 opacity-70" : "border-amber-500"}`}>
                  <div className="flex justify-between items-center gap-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-extrabold text-slate-100">🚛 {t.license_plate || t.id}</span>
                      {t.fleet_no && (
                        <>
                          <span className="text-slate-600">|</span>
                          <span className="text-slate-400 font-medium">Fleet {t.fleet_no}</span>
                        </>
                      )}
                      {t.make && (
                        <>
                          <span className="text-slate-600">|</span>
                          <span className="text-slate-400 font-medium">{t.make}</span>
                        </>
                      )}
                      <span className="text-slate-600">|</span>
                      <span className="text-slate-400 font-medium">Capacity: {t.capacity_cbm} cbm</span>
                      <span className="text-slate-600">|</span>
                      {/* The header of the printed checklist. Amber = due within
                          30 days, red = expired, so the office sees a lapsed
                          COF here before an inspector does. */}
                      {EXPIRY_FIELDS.map((f) => <ExpiryChip key={f.key} truck={t} field={f.key} label={f.label} />)}
                      {t.service_due && (
                        <span className="px-2 py-0.5 rounded border text-[10px] font-semibold text-slate-300 border-slate-700 bg-slate-800/40 whitespace-nowrap">
                          Service due {t.service_due}
                        </span>
                      )}
                      <span className="text-slate-600">|</span>
                      {retired ? (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-800 text-slate-400 border border-slate-700">retired</span>
                      ) : (
                        <span className={`font-semibold ${t.is_online ? "text-green-400" : "text-slate-500"}`}>
                          {t.is_online ? "🟢 Active" : "🔴 Parked"}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => (truckFor === t.id ? setTruckFor(null) : openTruck(t))} disabled={busy}
                        className="text-amber-300 hover:text-amber-200 px-2 py-1 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/25 rounded-lg transition-all font-semibold disabled:opacity-50"
                        title="Make, fleet number, TSL, COF / licence / RUC expiry, service due">
                        {truckFor === t.id ? "Close" : "Edit details"}
                      </button>
                    </div>
                  </div>

                  {truckFor === t.id && (
                    <form onSubmit={(e) => saveTruck(t, e)} className="bg-slate-950/60 border border-slate-800 rounded-xl p-3 space-y-2">
                      <p className="text-[10px] text-slate-400">
                        The vehicle header printed on every daily checklist for {t.license_plate || t.id}. Dates drive the
                        due / expired warnings on the Trucks tab. Turning "active" off retires the truck: drivers can no
                        longer pick it, and its checks are kept.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                        <label className="text-[10px] text-slate-400">
                          Make
                          <input value={truckDraft.make} onChange={(e) => setTruckDraft({ ...truckDraft, make: e.target.value })}
                            placeholder="e.g. Isuzu FVZ"
                            className="mt-1 w-full bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-amber-500" />
                        </label>
                        <label className="text-[10px] text-slate-400">
                          Fleet No
                          <input value={truckDraft.fleet_no} onChange={(e) => setTruckDraft({ ...truckDraft, fleet_no: e.target.value })}
                            className="mt-1 w-full bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-amber-500" />
                        </label>
                        <label className="text-[10px] text-slate-400">
                          TSL
                          <input value={truckDraft.tsl} onChange={(e) => setTruckDraft({ ...truckDraft, tsl: e.target.value })}
                            className="mt-1 w-full bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-amber-500" />
                        </label>
                        <label className="text-[10px] text-slate-400">
                          Service due
                          <input value={truckDraft.service_due} onChange={(e) => setTruckDraft({ ...truckDraft, service_due: e.target.value })}
                            placeholder="as written on the form, e.g. 245,000 km"
                            className="mt-1 w-full bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-amber-500" />
                        </label>
                        <label className="text-[10px] text-slate-400">
                          COF expiry
                          <input type="date" value={truckDraft.cof_expiry} onChange={(e) => setTruckDraft({ ...truckDraft, cof_expiry: e.target.value })}
                            className="mt-1 w-full bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-amber-500" />
                        </label>
                        <label className="text-[10px] text-slate-400">
                          Rego expiry
                          <input type="date" value={truckDraft.licence_expiry} onChange={(e) => setTruckDraft({ ...truckDraft, licence_expiry: e.target.value })}
                            className="mt-1 w-full bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-amber-500" />
                        </label>
                        <label className="text-[10px] text-slate-400">
                          RUC runs out at (km on hubo)
                          <input type="number" min="1" step="1000" value={truckDraft.ruc_expiry_km || ""} onChange={(e) => setTruckDraft({ ...truckDraft, ruc_expiry_km: e.target.value })}
                            placeholder="e.g. 260000"
                            className="mt-1 w-full bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-amber-500" />
                        </label>
                        {/* Scheduled maintenance the alerts act on. The km one is
                            measured against the latest hubo a driver entered on a
                            pre-check, so the truck raises its own service. */}
                        <label className="text-[10px] text-slate-400">
                          Next service date
                          <input type="date" value={truckDraft.next_service_date || ""} onChange={(e) => setTruckDraft({ ...truckDraft, next_service_date: e.target.value })}
                            className="mt-1 w-full bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-amber-500" />
                        </label>
                        <label className="text-[10px] text-slate-400">
                          Next service at (km on hubo)
                          <input type="number" min="1" step="100" value={truckDraft.next_service_km || ""} onChange={(e) => setTruckDraft({ ...truckDraft, next_service_km: e.target.value })}
                            placeholder="e.g. 250000"
                            className="mt-1 w-full bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-amber-500" />
                        </label>
                        <label className="text-[10px] text-slate-400 flex items-end gap-2 pb-2">
                          <input type="checkbox" checked={!!truckDraft.is_active}
                            onChange={(e) => setTruckDraft({ ...truckDraft, is_active: e.target.checked })} />
                          <span className="text-slate-300">Active (in the fleet)</span>
                        </label>
                        <label className="text-[10px] text-slate-400 md:col-span-3">
                          Notes
                          <input value={truckDraft.notes} onChange={(e) => setTruckDraft({ ...truckDraft, notes: e.target.value })}
                            placeholder="anything the office should know about this truck"
                            className="mt-1 w-full bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-amber-500" />
                        </label>
                        <div className="flex items-end gap-2">
                          <button type="submit" disabled={busy}
                            className="flex-1 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white font-semibold rounded-lg px-3 py-2">
                            Save details
                          </button>
                          <button type="button" onClick={() => setTruckFor(null)}
                            className="px-3 py-2 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700">
                            Cancel
                          </button>
                        </div>
                      </div>
                    </form>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Add Truck Form */}
        <div className="bg-slate-900/30 p-4 rounded-xl border border-slate-800/60 space-y-3">
          <p className="text-xs font-semibold text-slate-300">Register New Truck</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
            <input
              placeholder="License Plate"
              autoCapitalize="characters"
              className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-amber-500"
              value={newTruck.license_plate}
              onChange={e => setNewTruck({...newTruck, license_plate: e.target.value})}
            />
            <input
              placeholder="Fleet No"
              className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-amber-500"
              value={newTruck.fleet_no}
              onChange={e => setNewTruck({...newTruck, fleet_no: e.target.value})}
            />
            <input
              placeholder="Make (e.g. Isuzu FVZ)"
              className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-amber-500"
              value={newTruck.make}
              onChange={e => setNewTruck({...newTruck, make: e.target.value})}
            />
            <input
              placeholder="Volume Capacity (cbm)"
              type="number"
              className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-amber-500"
              value={newTruck.capacity_cbm}
              onChange={e => setNewTruck({...newTruck, capacity_cbm: parseFloat(e.target.value) || 50})}
            />
            <button
              onClick={addTruck}
              disabled={busy}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-4 py-2 font-bold transition-all flex items-center justify-center gap-1.5 shadow md:col-span-4 disabled:opacity-50"
            >
              <Plus size={14} /> Register Asset
            </button>
          </div>
          <div className="flex items-start gap-2 mt-2 text-[10px] text-slate-400 bg-slate-950/30 p-2.5 rounded-lg border border-slate-800">
            <Truck size={14} className="text-amber-400 shrink-0 mt-0.5" />
            <p>COF, licence and RUC expiry, TSL and service due are set with "Edit details" on the truck once it is registered. Trucks are retired, not deleted — their daily checks are kept for years.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
